const SALUT_REVIEWS_MONTHS = [
  'ian',
  'feb',
  'mar',
  'apr',
  'mai',
  'iun',
  'iul',
  'aug',
  'sep',
  'oct',
  'noi',
  'dec',
];

const SALUT_REVIEWS_AVATARS = [
  ['#CFDFF0', '#034B71'],
  ['#E6F7F0', '#00602A'],
  ['#F6E8BF', '#6B4E00'],
  ['#FFF0DF', '#8A4B00'],
  ['#FDE5E5', '#9B1C1C'],
];

function salutReviewsEscape(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (character) => {
    return {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[character];
  });
}

function salutReviewsParse(element) {
  if (!element) return null;
  try {
    return JSON.parse(element.textContent);
  } catch (error) {
    return null;
  }
}

function salutReviewsFold(value) {
  return String(value || '')
    .toLocaleLowerCase('ro-RO')
    .normalize('NFD')
    .split('')
    .filter((character) => {
      const code = character.codePointAt(0);
      return code < 768 || code > 879;
    })
    .join('');
}

function salutReviewsDate(value) {
  const parts = String(value).split('-');
  if (parts.length !== 3) return '';
  const month = SALUT_REVIEWS_MONTHS[Number(parts[1]) - 1] || '';
  return `${Number(parts[2])} ${month} ${parts[0]}`;
}

function salutReviewsInitial(name) {
  const trimmed = String(name || '').trim();
  return trimmed ? trimmed[0].toLocaleUpperCase('ro-RO') : '?';
}

function salutReviewsPalette(name) {
  let sum = 0;
  const value = String(name || '');
  for (let index = 0; index < value.length; index += 1) sum += value.charCodeAt(index);
  return SALUT_REVIEWS_AVATARS[sum % SALUT_REVIEWS_AVATARS.length];
}

function salutReviewsPhotos(review) {
  const list = Array.isArray(review && review.photos) ? review.photos : [];
  return list
    .filter(Boolean)
    .map((photo) => (typeof photo === 'string' ? { thumb: photo, full: photo } : photo))
    .filter((photo) => photo.thumb || photo.full)
    .map((photo) => ({ thumb: photo.thumb || photo.full, full: photo.full || photo.thumb }));
}

function salutReviewsAggregate(reviews) {
  const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  let total = 0;
  let sum = 0;
  let withPhotos = 0;
  let photos = 0;

  reviews.forEach((review) => {
    const rating = Math.min(5, Math.max(1, Math.round(Number(review.rating) || 0)));
    counts[rating] += 1;
    total += 1;
    sum += rating;
    const list = salutReviewsPhotos(review);
    if (list.length) {
      withPhotos += 1;
      photos += list.length;
    }
  });

  return {
    counts,
    total,
    photos,
    withPhotos,
    average: total ? sum / total : 0,
  };
}

class SalutReviews extends HTMLElement {
  connectedCallback() {
    this.labels = salutReviewsParse(this.querySelector('[data-salut-reviews-labels]')) || {};
    this.perPage = Number(this.labels.per_page) || 4;
    this.state = { filter: 'all', sort: 'popular', query: '', expanded: false };
    this.votes = new Map();

    this.grid = this.querySelector('[data-grid]');
    this.chipsRoot = this.querySelector('[data-chips]');
    this.summaryRoot = this.querySelector('[data-summary]');
    this.photosRoot = this.querySelector('[data-photos]');
    this.sortMenu = this.querySelector('[data-sort-menu]');
    this.sortToggle = this.querySelector('[data-sort-toggle]');
    this.searchInput = this.querySelector('[data-search]');
    this.moreButton = this.querySelector('[data-more]');
    this.extra = this.querySelector('[data-extra]');
    this.extraGrid = this.querySelector('[data-extra-grid]');
    this.dialog = this.querySelector('[data-dialog]');
    this.lightbox = this.querySelector('[data-lightbox]');
    this.starTemplate = this.querySelector('[data-icon-star]');
    this.checkTemplate = this.querySelector('[data-icon-check]');
    this.chevronTemplate = this.querySelector('[data-icon-chevron]');
    this.chevronDownTemplate = this.querySelector('[data-icon-chevron-down]');
    this.closeTemplate = this.querySelector('[data-icon-close]');

    this.load().then((data) => {
      this.reviews = Array.isArray(data && data.reviews) ? data.reviews : [];
      this.summary = (data && data.summary) || {};
      this.totals = salutReviewsAggregate(this.reviews);
      this.renderStatic();
      this.bind();
      this.bindLightbox();
      this.render();
    });
  }

  endpoints() {
    const raw = (this.dataset.endpoint || '').trim();
    if (!raw) return null;
    const [base, existingQuery] = raw.split('?');
    const clean = base.replace(/\/+$/, '');
    const product = this.dataset.product || '';
    const params = new URLSearchParams(existingQuery || '');
    if (product) params.set('product', product);
    const locale = (document.documentElement.lang || '').trim();
    if (locale) params.set('locale', locale);
    const query = params.toString();
    return {
      list: query ? `${clean}?${query}` : clean,
      submit: clean,
      vote: `${clean}/vote`,
    };
  }

  load() {
    const urls = this.endpoints();
    if (urls) {
      return fetch(urls.list)
        .then((response) => response.json())
        .catch(() => ({ reviews: [], summary: {} }));
    }
    return Promise.resolve(salutReviewsParse(this.querySelector('[data-salut-reviews]')) || {});
  }

  starsMarkup(rating, modifier) {
    const icon = this.starTemplate ? this.starTemplate.innerHTML : '';
    let markup = '';
    for (let index = 1; index <= 5; index += 1) {
      const empty = index > rating ? ' salut-reviews__star--empty' : '';
      markup += `<span class="salut-reviews__star${modifier}${empty}">${icon}</span>`;
    }
    return `<span class="salut-reviews__stars" role="img" aria-label="${rating}/5">${markup}</span>`;
  }

  countLabel(count) {
    const word = count === 1 ? this.labels.count_one : this.labels.count_many;
    return `${count} ${word || ''}`.trim();
  }

  renderStatic() {
    this.renderAi();
    this.renderSummary();
    this.renderPhotos();
    this.renderSortMenu();
  }

  renderAi() {
    const root = this.querySelector('[data-ai]');
    const hasSummary = Boolean(this.summary && this.summary.text);
    if (root) root.hidden = !hasSummary;
    if (!hasSummary) return;

    const body = this.querySelector('[data-ai-body]');
    const topics = this.querySelector('[data-ai-topics]');
    if (body) body.textContent = this.summary.text || '';
    if (!topics) return;
    const list = Array.isArray(this.summary.topics) ? this.summary.topics : [];
    if (!list.length) return;
    topics.hidden = false;
    topics.innerHTML = `
      <span class="salut-reviews__ai-topics-label">${salutReviewsEscape(this.labels.ai_topics_label)}</span>
      <span class="salut-reviews__ai-chips">
        ${list
          .map(
            (topic, index) =>
              `<span class="salut-reviews__ai-chip salut-reviews__ai-chip--${index % 5}">${salutReviewsEscape(
                topic
              )}</span>`
          )
          .join('')}
      </span>
    `;
  }

  renderSummary() {
    if (!this.summaryRoot) return;
    const { counts, total, average } = this.totals;
    const rows = [5, 4, 3, 2, 1]
      .map((star) => {
        const count = counts[star];
        const percent = total ? (count / total) * 100 : 0;
        return `
          <button class="salut-reviews__bar-row" type="button" data-filter="${star}" aria-pressed="${
          this.state.filter === String(star)
        }"${count ? '' : ' disabled'}>
            <span class="salut-reviews__bar-label">${star}★</span>
            <span class="salut-reviews__bar-track"><span class="salut-reviews__bar-fill" style="width: ${percent}%"></span></span>
            <span class="salut-reviews__bar-count">${count}</span>
          </button>
        `;
      })
      .join('');

    this.summaryRoot.innerHTML = `
      <div class="salut-reviews__score">
        <span class="salut-reviews__average">${average.toFixed(1)}</span>
        ${this.starsMarkup(Math.round(average), ' salut-reviews__star--lg')}
        <span class="salut-reviews__count">${salutReviewsEscape(this.countLabel(total))}</span>
      </div>
      <div class="salut-reviews__bars">${rows}</div>
      <div class="salut-reviews__bonus">
        <span class="salut-reviews__bonus-title">${salutReviewsEscape(this.labels.bonus_title)}</span>
        <span class="salut-reviews__bonus-text">${salutReviewsEscape(this.labels.bonus_text)}</span>
      </div>
    `;
  }

  renderPhotos() {
    if (!this.photosRoot) return;
    const photos = [];
    this.reviews.forEach((review) => {
      salutReviewsPhotos(review).forEach((photo, index) => {
        photos.push({ thumb: photo.thumb, full: photo.full, reviewId: review.id, index });
      });
    });
    if (!photos.length) return;

    this.allPhotos = photos;
    this.photosRoot.hidden = false;
    this.photosRoot.innerHTML = `
      <span class="salut-reviews__photos-title">${salutReviewsEscape(this.labels.photos_title)} · ${
      photos.length
    } foto</span>
      <div class="salut-reviews__photos-row" data-gallery="all" data-photos-row></div>
      <div class="salut-reviews__photos-panel" data-photos-grid data-expanded="false">
        <div class="salut-reviews__photos-panel-inner">
          <div class="salut-reviews__photos-row salut-reviews__photos-row--grid" data-gallery="all" data-photos-grid-row></div>
        </div>
      </div>
      <button class="salut-reviews__photos-link" type="button" data-photos-link aria-expanded="false">
        <span data-photos-link-label></span>
        <span class="salut-reviews__photos-chevron" data-photos-chevron hidden>${
          this.chevronDownTemplate ? this.chevronDownTemplate.innerHTML : ''
        }</span>
      </button>
    `;
    this.renderPhotosRow();
  }

  renderPhotosRow() {
    const row = this.photosRoot.querySelector('[data-photos-row]');
    const gridRow = this.photosRoot.querySelector('[data-photos-grid-row]');
    const link = this.photosRoot.querySelector('[data-photos-link]');
    const label = this.photosRoot.querySelector('[data-photos-link-label]');
    if (!row || !gridRow || !link || !label) return;

    const photos = this.allPhotos || [];
    const expanded = Boolean(this.photosExpanded);
    if (!row.children.length) {
      row.innerHTML = photos.slice(0, 8).map((photo, index) => this.photoMarkup(photo, index)).join('');
      gridRow.innerHTML = photos
        .slice(8)
        .map((photo, index) => this.photoMarkup(photo, index + 8))
        .join('');
    }
    this.photosRoot.querySelector('[data-photos-grid]').setAttribute('data-expanded', String(expanded));
    link.hidden = photos.length <= 8;
    link.setAttribute('aria-expanded', String(expanded));
    link.classList.toggle('salut-reviews__photos-link--open', expanded);
    const chevron = this.photosRoot.querySelector('[data-photos-chevron]');
    if (chevron) chevron.hidden = !expanded;
    label.textContent = expanded
      ? this.labels.photos_less
      : String(this.labels.photos_link || '').replace('[count]', photos.length);
  }

  reviewById(id) {
    return this.reviews.find((review) => review.id === id);
  }

  photoMarkup(photo, index) {
    return `
      <button class="salut-reviews__photo" type="button" data-photo="${index}" aria-label="${salutReviewsEscape(
      this.labels.photo_open
    )}">
        <img src="${salutReviewsEscape(photo.thumb)}" alt="" loading="lazy" width="96" height="96">
      </button>
    `;
  }

  renderSortMenu() {
    if (!this.sortMenu) return;
    const options = [
      ['popular', this.labels.sort_popular],
      ['newest', this.labels.sort_newest],
      ['highest', this.labels.sort_highest],
      ['lowest', this.labels.sort_lowest],
    ];
    this.sortMenu.innerHTML = options
      .map(
        ([value, label]) =>
          `<button class="salut-reviews__sort-item" type="button" role="menuitemradio" data-sort="${value}" aria-checked="${
            this.state.sort === value
          }">${salutReviewsEscape(label)}</button>`
      )
      .join('');
  }

  renderChips() {
    if (!this.chipsRoot) return;
    const { counts, total, withPhotos } = this.totals;
    const chips = [
      ['all', `${this.labels.filter_all} (${total})`],
      ['photos', `${this.labels.filter_photos} (${withPhotos})`],
      ['5', `5★ (${counts[5]})`],
      ['4', `4★ (${counts[4]})`],
      ['3', `3★ (${counts[3]})`],
      ['2', `2★ (${counts[2]})`],
      ['1', `1★ (${counts[1]})`],
    ];
    this.chipsRoot.innerHTML = chips
      .map(
        ([value, label]) =>
          `<button class="salut-reviews__chip" type="button" data-filter="${value}" aria-pressed="${
            this.state.filter === value
          }">${salutReviewsEscape(label)}</button>`
      )
      .join('');
  }

  visibleReviews() {
    const query = salutReviewsFold(this.state.query.trim());
    let list = this.reviews.filter((review) => {
      const photos = salutReviewsPhotos(review);
      if (this.state.filter === 'photos' && !photos.length) return false;
      if (/^[1-5]$/.test(this.state.filter) && Math.round(review.rating) !== Number(this.state.filter)) {
        return false;
      }
      if (!query) return true;
      return salutReviewsFold([review.title, review.body, review.author, review.city].join(' ')).includes(query);
    });

    const sorters = {
      popular: (a, b) => this.helpfulOf(b) - this.helpfulOf(a),
      newest: (a, b) => String(b.date).localeCompare(String(a.date)),
      highest: (a, b) => b.rating - a.rating || String(b.date).localeCompare(String(a.date)),
      lowest: (a, b) => a.rating - b.rating || String(b.date).localeCompare(String(a.date)),
    };
    list = list.slice().sort(sorters[this.state.sort] || sorters.popular);
    return list;
  }

  helpfulOf(review) {
    const vote = this.votes.get(review.id);
    return (Number(review.helpful) || 0) + (vote === 'up' ? 1 : 0);
  }

  cardMarkup(review) {
    const photos = salutReviewsPhotos(review);
    const [background, color] = salutReviewsPalette(review.author);
    const check = this.checkTemplate ? this.checkTemplate.innerHTML : '';

    return `
      <article class="salut-reviews__card">
        <div class="salut-reviews__card-head">
          <span class="salut-reviews__avatar" style="--salut-avatar-bg: ${background}; --salut-avatar-fg: ${color};" aria-hidden="true">${salutReviewsEscape(
      salutReviewsInitial(review.author)
    )}</span>
          <div class="salut-reviews__identity">
            <span class="salut-reviews__author">${salutReviewsEscape(review.author)}</span>
            <span class="salut-reviews__origin">${salutReviewsEscape(review.city)} · ${salutReviewsEscape(
      salutReviewsDate(review.date)
    )}</span>
          </div>
          <div class="salut-reviews__badges">
            ${
              review.verified
                ? `<span class="salut-reviews__verified"><span class="salut-reviews__verified-icon">${check}</span>${salutReviewsEscape(
                    this.labels.verified
                  )}</span>`
                : ''
            }
            ${this.starsMarkup(Math.round(review.rating), '')}
          </div>
        </div>
        <div class="salut-reviews__body">
          <h3 class="salut-reviews__card-title">${salutReviewsEscape(review.title)}</h3>
          <p class="salut-reviews__text">${salutReviewsEscape(review.body)}</p>
          ${
            photos.length
              ? `<div class="salut-reviews__card-photos" data-gallery="${salutReviewsEscape(review.id)}">${photos
                  .map((photo, index) => this.photoMarkup(photo, index))
                  .join('')}</div>`
              : ''
          }
        </div>
        <div class="salut-reviews__helpful">${this.votesMarkup(review)}</div>
      </article>
    `;
  }

  render() {
    this.renderChips();
    const list = this.visibleReviews();

    if (!list.length) {
      this.grid.classList.add('salut-reviews__grid--empty');
      this.grid.innerHTML = `<p class="salut-reviews__empty">${salutReviewsEscape(this.labels.empty)}</p>`;
    } else {
      this.grid.classList.remove('salut-reviews__grid--empty');
      this.grid.innerHTML = list
        .slice(0, this.perPage)
        .map((review) => this.cardMarkup(review))
        .join('');
    }

    if (this.extraGrid) {
      this.extraGrid.innerHTML = list
        .slice(this.perPage)
        .map((review) => this.cardMarkup(review))
        .join('');
    }
    if (this.extra) {
      this.extra.setAttribute('data-expanded', String(this.state.expanded));
    }

    if (this.summaryRoot) {
      this.summaryRoot.querySelectorAll('[data-filter]').forEach((row) => {
        row.setAttribute('aria-pressed', String(this.state.filter === row.dataset.filter));
      });
    }

    if (this.moreButton) {
      this.moreButton.hidden = list.length <= this.perPage;
      this.moreButton.classList.toggle('salut-reviews__more--open', this.state.expanded);
      this.moreButton.setAttribute('aria-expanded', String(this.state.expanded));
      const label = this.moreButton.querySelector('[data-more-label]');
      if (label) {
        label.textContent = this.state.expanded ? this.labels.show_less : this.labels.show_all;
      }
    }
  }

  bind() {
    this.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-filter]');
      if (chip) {
        const value = chip.dataset.filter;
        this.state.filter = this.state.filter === value ? 'all' : value;
        this.state.expanded = false;
        this.render();
        return;
      }

      const sort = event.target.closest('[data-sort]');
      if (sort) {
        this.state.sort = sort.dataset.sort;
        this.closeSort();
        this.renderSortMenu();
        this.render();
        return;
      }

      if (event.target.closest('[data-sort-toggle]')) {
        this.toggleSort();
        return;
      }

      if (event.target.closest('[data-photos-link]')) {
        this.photosExpanded = !this.photosExpanded;
        this.renderPhotosRow();
        return;
      }

      const photo = event.target.closest('[data-photo]');
      if (photo) {
        const gallery = photo.closest('[data-gallery]');
        const position = Number(photo.dataset.photo) || 0;
        if (gallery.dataset.gallery === 'all') {
          const entry = (this.allPhotos || [])[position];
          if (entry) this.openLightbox(this.reviewById(entry.reviewId), entry.index, photo);
        } else {
          this.openLightbox(this.reviewById(gallery.dataset.gallery), position, photo);
        }
        return;
      }

      if (event.target.closest('[data-more]')) {
        this.state.expanded = !this.state.expanded;
        this.render();
        return;
      }

      const vote = event.target.closest('[data-vote]');
      if (vote) {
        const current = this.votes.get(vote.dataset.id);
        if (current === vote.dataset.vote) {
          this.votes.delete(vote.dataset.id);
        } else {
          this.votes.set(vote.dataset.id, vote.dataset.vote);
        }
        this.render();
        this.renderLightboxVotes();

        const urls = this.endpoints();
        if (urls && this.votes.get(vote.dataset.id) === vote.dataset.vote) {
          const body = new FormData();
          body.set('review', vote.dataset.id);
          body.set('vote', vote.dataset.vote);
          // Un vot pierdut nu merită un mesaj de eroare: starea locală rămâne cum a apăsat
          // clientul, iar numărul real vine oricum la următoarea încărcare a paginii.
          fetch(urls.vote, { method: 'POST', body }).catch(() => {});
        }
        return;
      }

      if (event.target.closest('[data-write]')) {
        this.openDialog();
      }
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => {
        this.state.query = this.searchInput.value;
        this.state.expanded = false;
        this.render();
      });
    }

    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.closeSort();
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeSort();
    });
  }

  toggleSort() {
    if (!this.sortMenu) return;
    if (this.sortMenu.hidden) {
      this.sortMenu.hidden = false;
      this.sortToggle.setAttribute('aria-expanded', 'true');
    } else {
      this.closeSort();
    }
  }

  closeSort() {
    if (!this.sortMenu || this.sortMenu.hidden) return;
    this.sortMenu.hidden = true;
    this.sortToggle.setAttribute('aria-expanded', 'false');
  }

  openLightbox(review, index, opener) {
    const photos = review ? salutReviewsPhotos(review) : [];
    if (!this.lightbox || !photos.length) return;
    this.lightboxReview = review;
    this.lightboxPhotos = photos;
    this.lightboxIndex = Math.min(Math.max(index, 0), photos.length - 1);
    this.lightboxOpener = opener || null;
    this.renderLightbox();
    if (!this.lightbox.open) this.lightbox.showModal();
    document.body.classList.add('salut-reviews-locked');
    const inner = this.lightbox.querySelector('.salut-reviews__lightbox-inner');
    if (inner) inner.focus();
    requestAnimationFrame(() => this.updateThumbArrows());
  }

  renderLightbox() {
    const review = this.lightboxReview;
    const photo = this.lightboxPhotos[this.lightboxIndex];
    const many = this.lightboxPhotos.length > 1;
    const [background, color] = salutReviewsPalette(review.author);
    const check = this.checkTemplate ? this.checkTemplate.innerHTML : '';

    this.lightbox.innerHTML = `
      <div class="salut-reviews__lightbox-inner" tabindex="-1">
        <button class="salut-reviews__lightbox-close" type="button" data-close aria-label="${salutReviewsEscape(
          this.labels.photo_close
        )}">${this.closeTemplate ? this.closeTemplate.innerHTML : ''}</button>
        <img class="salut-reviews__lightbox-image" src="${salutReviewsEscape(photo.full)}" alt="" data-image>
        <div class="salut-reviews__card-head salut-reviews__lightbox-head">
          <span class="salut-reviews__avatar" style="--salut-avatar-bg: ${background}; --salut-avatar-fg: ${color};" aria-hidden="true">${salutReviewsEscape(
      salutReviewsInitial(review.author)
    )}</span>
          <div class="salut-reviews__identity">
            <span class="salut-reviews__author">${salutReviewsEscape(review.author)}</span>
            <span class="salut-reviews__origin">${salutReviewsEscape(review.city)} · ${salutReviewsEscape(
      salutReviewsDate(review.date)
    )}</span>
          </div>
          <div class="salut-reviews__badges">
            ${
              review.verified
                ? `<span class="salut-reviews__verified"><span class="salut-reviews__verified-icon">${check}</span>${salutReviewsEscape(
                    this.labels.verified
                  )}</span>`
                : ''
            }
            ${this.starsMarkup(Math.round(review.rating), '')}
          </div>
        </div>
        <div class="salut-reviews__lightbox-body">
          <h3 class="salut-reviews__card-title">${salutReviewsEscape(review.title)}</h3>
          <p class="salut-reviews__text">${salutReviewsEscape(review.body)}</p>
        </div>
        <div class="salut-reviews__helpful" data-lightbox-votes>${this.votesMarkup(review)}</div>
        ${
          many
            ? `
              <span class="salut-reviews__lightbox-more">${salutReviewsEscape(this.labels.photo_more)}</span>
              <div class="salut-reviews__lightbox-thumbs">
                <button class="salut-reviews__lightbox-arrow salut-reviews__lightbox-arrow--prev" type="button" data-thumb-step="-1" aria-label="${salutReviewsEscape(
                  this.labels.photo_prev
                )}" hidden>${this.chevronMarkup()}</button>
                <div class="salut-reviews__lightbox-track" data-thumb-track>
                  ${this.lightboxPhotos
                    .map(
                      (item, index) =>
                        `<button class="salut-reviews__photo salut-reviews__photo--thumb" type="button" data-thumb="${index}" aria-label="${salutReviewsEscape(
                          this.labels.photo_open
                        )}" aria-pressed="${index === this.lightboxIndex}"><img src="${salutReviewsEscape(
                          item.thumb
                        )}" alt="" loading="lazy" width="96" height="96"></button>`
                    )
                    .join('')}
                </div>
                <button class="salut-reviews__lightbox-arrow salut-reviews__lightbox-arrow--next" type="button" data-thumb-step="1" aria-label="${salutReviewsEscape(
                  this.labels.photo_next
                )}" hidden>${this.chevronMarkup()}</button>
              </div>
            `
            : ''
        }
      </div>
    `;
  }

  votesMarkup(review) {
    const vote = this.votes.get(review.id);
    return `
      <span class="salut-reviews__helpful-label">${salutReviewsEscape(this.labels.helpful)}</span>
      <button class="salut-reviews__vote${
        vote === 'up' ? ' salut-reviews__vote--active' : ''
      }" type="button" data-vote="up" data-id="${salutReviewsEscape(review.id)}">👍 ${this.helpfulOf(
      review
    )}</button>
      <button class="salut-reviews__vote${
        vote === 'down' ? ' salut-reviews__vote--active' : ''
      }" type="button" data-vote="down" data-id="${salutReviewsEscape(review.id)}">👎 ${
      vote === 'down' ? 1 : 0
    }</button>
    `;
  }

  renderLightboxVotes() {
    if (!this.lightbox || !this.lightbox.open || !this.lightboxReview) return;
    const root = this.lightbox.querySelector('[data-lightbox-votes]');
    if (root) root.innerHTML = this.votesMarkup(this.lightboxReview);
  }

  chevronMarkup() {
    return this.chevronTemplate ? this.chevronTemplate.innerHTML : '';
  }

  stepLightbox(step) {
    const count = this.lightboxPhotos.length;
    this.showPhoto((this.lightboxIndex + step + count) % count);
  }

  showPhoto(index) {
    if (index === this.lightboxIndex) return;
    this.lightboxIndex = index;
    const image = this.lightbox.querySelector('[data-image]');
    if (image) {
      const fresh = image.cloneNode(false);
      fresh.src = this.lightboxPhotos[index].full;
      image.replaceWith(fresh);
    }
    this.lightbox.querySelectorAll('[data-thumb]').forEach((thumb) => {
      const active = Number(thumb.dataset.thumb) === index;
      thumb.setAttribute('aria-pressed', String(active));
      if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    });
  }

  updateThumbArrows() {
    if (!this.lightbox || !this.lightbox.open) return;
    const track = this.lightbox.querySelector('[data-thumb-track]');
    if (!track) return;
    const overflow = track.scrollWidth - track.clientWidth > 1;
    this.lightbox.querySelectorAll('[data-thumb-step]').forEach((arrow) => {
      arrow.hidden = !overflow;
    });
  }

  bindLightbox() {
    if (!this.lightbox) return;
    this.lightbox.addEventListener('click', (event) => {
      if (this.swiped) {
        this.swiped = false;
        return;
      }

      const arrow = event.target.closest('[data-thumb-step]');
      if (arrow) {
        const track = this.lightbox.querySelector('[data-thumb-track]');
        if (track) track.scrollBy({ left: Number(arrow.dataset.thumbStep) * 212, behavior: 'smooth' });
        return;
      }

      const thumb = event.target.closest('[data-thumb]');
      if (thumb) {
        this.showPhoto(Number(thumb.dataset.thumb) || 0);
        return;
      }

      if (event.target.closest('[data-close]') || !event.target.closest('.salut-reviews__lightbox-inner')) {
        this.lightbox.close();
      }
    });

    this.lightbox.addEventListener('keydown', (event) => {
      if (this.lightboxPhotos.length < 2) return;
      if (event.key === 'ArrowRight') this.stepLightbox(1);
      if (event.key === 'ArrowLeft') this.stepLightbox(-1);
    });

    this.lightbox.addEventListener('close', () => {
      document.body.classList.remove('salut-reviews-locked');
      if (this.lightboxOpener && this.lightboxOpener.isConnected) this.lightboxOpener.focus();
      this.lightboxOpener = null;
    });

    window.addEventListener('resize', () => this.updateThumbArrows());

    let swipeStart = null;
    this.lightbox.addEventListener('pointerdown', (event) => {
      swipeStart = event.target.closest('.salut-reviews__lightbox-image') ? event.clientX : null;
      this.swiped = false;
    });
    this.lightbox.addEventListener('pointerup', (event) => {
      if (swipeStart === null || this.lightboxPhotos.length < 2) return;
      const distance = event.clientX - swipeStart;
      swipeStart = null;
      if (Math.abs(distance) <= 40) return;
      this.swiped = true;
      this.stepLightbox(distance < 0 ? 1 : -1);
    });
  }

  openDialog() {
    if (!this.dialog) return;
    this.dialog.innerHTML = this.formMarkup();
    this.dialog.showModal();

    const form = this.dialog.querySelector('form');
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const urls = this.endpoints();
      if (!urls) return;

      const submitButton = form.querySelector('.salut-reviews__form-submit');
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.classList.add('salut-reviews__form-submit--busy');
      }
      const previousError = form.querySelector('[data-form-error]');
      if (previousError) previousError.remove();

      const payload = new FormData(form);
      payload.set('product', this.dataset.product || '');

      fetch(urls.submit, { method: 'POST', body: payload })
        .then((response) =>
          response
            .json()
            .catch(() => null)
            .then((data) => ({ ok: response.ok, data }))
        )
        .then(({ ok, data }) => {
          if (!ok || !data || !data.ok) {
            const messages = data && data.errors ? Object.values(data.errors) : [];
            const error = new Error(messages.join(' ') || this.labels.form_error);
            error.isServerMessage = messages.length > 0;
            throw error;
          }
          const photoWarning =
            data.warnings && data.warnings.photos
              ? `<p class="salut-reviews__form-warning">${salutReviewsEscape(data.warnings.photos)}</p>`
              : '';
          this.dialog.innerHTML = `
            <div class="salut-reviews__form-thanks">
              <p>${salutReviewsEscape(this.labels.form_thanks)}</p>
              ${photoWarning}
              <button class="salut-reviews__form-submit" type="button" data-close>OK</button>
            </div>
          `;
        })
        .catch((error) => {
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.classList.remove('salut-reviews__form-submit--busy');
          }
          const notice = document.createElement('p');
          notice.className = 'salut-reviews__form-error';
          notice.setAttribute('data-form-error', '');
          notice.setAttribute('role', 'alert');
          notice.textContent = error && error.isServerMessage ? error.message : this.labels.form_error;
          form.querySelector('.salut-reviews__form-actions').before(notice);
        });
    });

    this.dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-close]') || event.target === this.dialog) this.dialog.close();
      const star = event.target.closest('[data-star-value]');
      if (star) {
        const value = Number(star.dataset.starValue);
        this.dialog.querySelectorAll('[data-star-value]').forEach((button) => {
          button.classList.toggle('salut-reviews__form-star--on', Number(button.dataset.starValue) <= value);
        });
        const ratingInput = this.dialog.querySelector('input[name="rating"]');
        if (ratingInput) ratingInput.value = String(value);
      }
    });
  }

  formMarkup() {
    const icon = this.starTemplate ? this.starTemplate.innerHTML : '';
    const stars = [1, 2, 3, 4, 5]
      .map(
        (value) =>
          `<button class="salut-reviews__form-star salut-reviews__form-star--on" type="button" data-star-value="${value}" aria-label="${value}/5">${icon}</button>`
      )
      .join('');

    return `
      <form class="salut-reviews__form" method="dialog">
        <input type="hidden" name="rating" value="5">
        <div class="salut-reviews__form-head">
          <h3 class="salut-reviews__form-title">${salutReviewsEscape(this.labels.form_title)}</h3>
          <button class="salut-reviews__form-close" type="button" data-close aria-label="${salutReviewsEscape(
            this.labels.form_cancel
          )}">✕</button>
        </div>
        <label class="salut-reviews__form-label">${salutReviewsEscape(this.labels.form_rating)}</label>
        <div class="salut-reviews__form-stars">${stars}</div>
        <div class="salut-reviews__form-row">
          <label class="salut-reviews__form-field">
            <span class="salut-reviews__form-label">${salutReviewsEscape(this.labels.form_name)}</span>
            <input type="text" name="name" required>
          </label>
          <label class="salut-reviews__form-field">
            <span class="salut-reviews__form-label">${salutReviewsEscape(this.labels.form_city)}</span>
            <input type="text" name="city">
          </label>
        </div>
        <label class="salut-reviews__form-field">
          <span class="salut-reviews__form-label">${salutReviewsEscape(this.labels.form_headline)}</span>
          <input type="text" name="headline" required>
        </label>
        <label class="salut-reviews__form-field">
          <span class="salut-reviews__form-label">${salutReviewsEscape(this.labels.form_body)}</span>
          <textarea name="body" rows="4" required></textarea>
        </label>
        <label class="salut-reviews__form-field">
          <span class="salut-reviews__form-label">${salutReviewsEscape(this.labels.form_photos)}</span>
          <input type="file" name="photos" accept="image/*" multiple>
        </label>
        <div class="salut-reviews__form-actions">
          <button class="salut-reviews__form-cancel" type="button" data-close>${salutReviewsEscape(
            this.labels.form_cancel
          )}</button>
          <button class="salut-reviews__form-submit" type="submit">${salutReviewsEscape(
            this.labels.form_submit
          )}</button>
        </div>
      </form>
    `;
  }
}

if (!customElements.get('salut-reviews')) {
  customElements.define('salut-reviews', SalutReviews);
}
